import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import { Settings } from 'lucide-react';
import { SpaceWeatherSettingsDialog } from './SpaceWeatherSettingsDialog';
import { format } from 'date-fns';
import { collection, getDocs, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { buildAppsationsFunctionUrl } from '@/lib/appsationsSdkEndpoints';
// Do not import db at module scope to avoid circular import issues
const getDb = async () => {
  const { db } = await import('@/lib/firebase');
  return db;
};

interface SpaceWeatherItem {
  issue_datetime?: string;
  message?: string;
  product_id?: string;
  [key: string]: any;
}

function parseRss(xml: string): SpaceWeatherItem[] {
  const parser = new window.DOMParser();
  const doc = parser.parseFromString(xml, 'application/xml');
  const items = Array.from(doc.querySelectorAll('item'));
  return items.map(item => ({
    title: item.querySelector('title')?.textContent || '',
    pubDate: item.querySelector('pubDate')?.textContent || '',
    description: item.querySelector('description')?.textContent || '',
    link: item.querySelector('link')?.textContent || '',
  }));
}

export const SpaceWeatherWidget: FC<{ timelineDate?: Date; isVisible?: boolean }> = ({ timelineDate, isVisible = true }) => {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [report, setReport] = useState<SpaceWeatherItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchingHistory, setFetchingHistory] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [history, setHistory] = useState<SpaceWeatherItem[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillError, setBackfillError] = useState<string | null>(null);
  const lastAutoFetchDateRef = useRef<string | null>(null);

  const dateStr = timelineDate ? timelineDate.toISOString().slice(0, 10) : undefined;

  const fetchHistory = async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const db = await getDb();
      if (!db) throw new Error('Firestore not configured.');
      const baseRef = collection(db, 'spaceWeatherReports');
      const startDate = new Date();
      startDate.setFullYear(startDate.getFullYear() - 5);
      const startIso = startDate.toISOString();
      const snap = await getDocs(
        query(baseRef, where('pubDate', '>=', startIso), orderBy('pubDate', 'desc'), limit(2000))
      );
      if (!snap.empty) {
        setHistory(snap.docs.map(doc => doc.data() as SpaceWeatherItem));
        return;
      }
      const fallbackSnap = await getDocs(
        query(baseRef, where('issue_datetime', '>=', startIso), orderBy('issue_datetime', 'desc'), limit(2000))
      );
      const fallbackItems = fallbackSnap.docs.map(doc => doc.data() as SpaceWeatherItem);
      setHistory(fallbackItems.sort((a, b) => {
        const aDate = a.issue_datetime ?? a.pubDate ?? '';
        const bDate = b.issue_datetime ?? b.pubDate ?? '';
        return bDate.localeCompare(aDate);
      }));
    } catch (e: any) {
      setHistoryError('Failed to load history. ' + (e?.message || e));
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchAndSaveHistory = useCallback(async () => {
    if (!dateStr) return;
    setFetchingHistory(true);
    setError(null);
    try {
      const fallbackProjectId = (import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '').trim();
      const sameOriginRewrite = '/space-weather-by-date';
      const primaryBaseUrl = buildAppsationsFunctionUrl('getSpaceWeatherByDate');
      const fallbackBaseUrl = fallbackProjectId
        ? `https://us-central1-${fallbackProjectId}.cloudfunctions.net/getSpaceWeatherByDate`
        : '';
      const endpointCandidates = Array.from(new Set([sameOriginRewrite, primaryBaseUrl, fallbackBaseUrl].filter(Boolean)));

      let lastError: unknown = null;
      let succeeded = false;
      for (const endpoint of endpointCandidates) {
        try {
          const url = `${endpoint}?date=${encodeURIComponent(dateStr)}`;
          const res = await fetch(url, { cache: 'no-store' });
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error((data as any).error || `HTTP ${res.status}`);
          }
          succeeded = true;
          break;
        } catch (candidateError) {
          lastError = candidateError;
        }
      }

      if (!succeeded) {
        throw lastError instanceof Error ? lastError : new Error('Unable to reach space weather endpoint.');
      }

      setRefreshKey(k => k + 1);
    } catch (e: any) {
      const msg: string = e?.message ?? String(e);
      // Suppress network errors silently when Firestore already has data
      if (!msg.toLowerCase().includes('networkerror') && !msg.toLowerCase().includes('failed to fetch')) {
        if (!report) setError('Space weather data unavailable for this date.');
      }
    } finally {
      setFetchingHistory(false);
    }
  }, [dateStr, report]);

  useEffect(() => {
    if (!dateStr || !isVisible) return;
    setLoading(true);
    setError(null);
    let unsubscribe = () => {};
    let cancelled = false;
    (async () => {
      const db = await getDb();
      if (!db) {
        if (!cancelled) { setError('Firestore not configured.'); setLoading(false); }
        return;
      }
      const baseRef = collection(db, 'spaceWeatherReports');
      unsubscribe = onSnapshot(
        baseRef,
        snap => {
          const matches: SpaceWeatherItem[] = [];
          snap.forEach(doc => {
            const data = doc.data() as SpaceWeatherItem;
            const pubDate = data.pubDate ? data.pubDate.slice(0, 10) : undefined;
            const issueDate = data.issue_datetime ? data.issue_datetime.slice(0, 10) : undefined;
            if (pubDate === dateStr || issueDate === dateStr) matches.push(data);
          });
          if (matches.length > 0) {
            matches.sort((a, b) => {
              const aDate = a.issue_datetime ?? a.pubDate ?? '';
              const bDate = b.issue_datetime ?? b.pubDate ?? '';
              return bDate.localeCompare(aDate);
            });
            setReport(matches[0]);
          } else {
            setReport(null);
          }
          setLoading(false);
        },
        e => {
          setError('Failed to query space weather history. ' + (e?.message || e));
          setLoading(false);
        }
      );
    })();
    return () => { cancelled = true; unsubscribe(); };
  }, [dateStr, isVisible, refreshKey]);

  useEffect(() => {
    if (!dateStr || !isVisible) return;
    const todayStr = new Date().toISOString().slice(0, 10);
    if (dateStr !== todayStr) return;
    if (lastAutoFetchDateRef.current === dateStr) return;
    lastAutoFetchDateRef.current = dateStr;
    fetchAndSaveHistory();
  }, [dateStr, isVisible, fetchAndSaveHistory]);

  useEffect(() => {
    const handler = () => { setShowHistory(true); fetchHistory(); };
    window.addEventListener('showSpaceWeatherHistory', handler);
    return () => window.removeEventListener('showSpaceWeatherHistory', handler);
  }, []);

  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-black/30 backdrop-blur-md p-4 text-white shadow-lg min-h-[120px] flex flex-col gap-2">
      <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-900/20 via-transparent to-blue-900/20" />

      <div className="relative flex items-center mb-1 pb-2 border-b border-white/10">
        <button
          onClick={() => setSettingsOpen(true)}
          className="mr-2 p-1.5 rounded-full bg-white/10 border border-white/10 shadow-sm hover:bg-white/20 transition-colors"
          title="Space Weather Settings"
          aria-label="Space Weather Settings"
        >
          <Settings className="w-5 h-5 text-white/70" />
        </button>
        <span role="img" aria-label="space" className="mr-2">🛰️</span>
        <div className="font-bold text-lg flex items-center gap-2 text-white">
          Space Weather
          {dateStr && <span className="ml-2 text-xs text-white/50">({dateStr})</span>}
        </div>
      </div>

      <SpaceWeatherSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />

      <div className="relative flex flex-wrap items-center gap-2">
        <button
          className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-xs text-white/70 border border-white/10 transition-colors"
          onClick={() => { setShowHistory(h => !h); if (!showHistory) fetchHistory(); }}
        >
          {showHistory ? 'Hide History' : 'Show History'}
        </button>
      </div>

      {loading && <div className="text-white/50 text-sm">Loading…</div>}

      {error && (
        <div className="text-red-300 text-sm">{error}</div>
      )}

      {report && !loading && !error && (
        <div className="relative">
          <div className="font-semibold text-white">
            {report.product_id ? <span className="mr-2 text-cyan-300">[{report.product_id}]</span> : null}
            NOAA Space Weather Alert
          </div>
          {report.issue_datetime && (
            <div className="text-xs text-white/50 mb-1">{format(new Date(report.issue_datetime), 'PPpp')}</div>
          )}
          {report.message ? (
            <div className="max-h-48 overflow-y-auto">
              <pre className="text-sm whitespace-pre-wrap text-white/80 bg-white/5 rounded-lg p-2 mt-1 border border-white/10">{report.message}</pre>
              {report.forecast && (
                <div className="mt-2 text-cyan-300 bg-cyan-900/20 rounded-lg p-2 border border-cyan-400/20">
                  <div className="font-semibold mb-1">Forecast</div>
                  <pre className="text-xs whitespace-pre-wrap">{typeof report.forecast === 'string' ? report.forecast : JSON.stringify(report.forecast, null, 2)}</pre>
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-white/50">No alert message available.</div>
          )}
        </div>
      )}

      {!report && !loading && !error && (
        <div className="text-white/50 text-sm">
          No space weather data for <span className="font-mono text-white/70">{dateStr}</span>.
        </div>
      )}

      {backfillError && <div className="text-red-300 text-sm">{backfillError}</div>}

      {showHistory && (
        <div className="relative mt-2 bg-white/5 rounded-xl p-2 max-h-64 overflow-y-auto border border-white/10">
          <div className="font-semibold text-white/60 mb-1 text-xs uppercase tracking-wide">Recent History</div>
          {historyLoading && <div className="text-white/40 text-sm">Loading history…</div>}
          {historyError && <div className="text-red-300 text-sm">{historyError}</div>}
          {!historyLoading && !historyError && history.length === 0 && (
            <div className="text-white/40 text-sm">No history found.</div>
          )}
          {!historyLoading && !historyError && history.length > 0 && (
            <ul className="space-y-1 text-xs">
              {history.map((item, i) => (
                <li key={item.issue_datetime || item.pubDate || i} className="border-b border-white/10 pb-1 mb-1">
                  <span className="text-white/80 font-mono">{item.issue_datetime ? format(new Date(item.issue_datetime), 'yyyy-MM-dd') : (item.pubDate || '')}</span>
                  {item.product_id && <span className="ml-2 text-cyan-300">[{item.product_id}]</span>}
                  {item.message && <span className="ml-2 text-white/40">{item.message.slice(0, 60)}{item.message.length > 60 ? '…' : ''}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
